import { getDevAdminHostOverride, getDevClubOverride } from "@/lib/api";

/**
 * Three-way host taxonomy — the client mirror of the backend
 * `club_context.resolve_host_kind`:
 *
 *   - `"club"`  — a real club subdomain (`<slug>.tktic.com`), OR any session
 *     carrying a dev club override. The full app lives here.
 *   - `"admin"` — the OPERATOR host (`admin.tktic.com`), and the only host that
 *     serves the platform console: login-only for anonymous visitors, and a
 *     signed-in super-admin lands on `/super-admin`. No marketing/public pages.
 *     Never write "apex / super-admin host" for this — the apex is a different
 *     host and does NOT serve the console (that ambiguity caused #593).
 *   - `"apex"`  — the bare apex / `www` / a foreign/dev-localhost host.
 *     Marketing landing only; NO login, NO app routes (`/teams` etc. 404), and
 *     NO operator console.
 *
 * Dev/e2e caveat: single-localhost has no subdomains, so bare localhost is
 * `apex`. Two DEV-only overrides let it impersonate the other two —
 * `VITE_DEV_CLUB_OVERRIDE` (→ `club`) and `VITE_DEV_ADMIN_HOST_OVERRIDE`
 * (→ `admin`). The club override is checked first; both are stripped from
 * production bundles.
 *
 * This drives ROUTING only — never data authorization. The server remains the
 * authoritative gate for every club-scoped read/write; this just decides where
 * to send the browser. The host kind is computed from `window.location.hostname`
 * against `apexDomain()` — which prefers the apex the BACKEND reported over the
 * one baked at build time (see `adoptApexDomain`), so a wrong build arg cannot
 * misclassify a live deployment. That reconciliation happens once, anonymously,
 * from `/api/settings/public`, which is why it also protects signed-out surfaces
 * like `/login`; `/api/me.host_kind` is the same fact for signed-in flows.
 */
export type HostKind = "club" | "admin" | "apex";

// The apex the BUNDLE was built with. Correct in every environment CI builds,
// but a hand-rolled `docker build` can bake the default against a deployment
// that uses a different apex — see `adoptApexDomain` for how that is repaired.
function bakedApexDomain(): string {
  return (import.meta.env.VITE_CLUB_APEX_DOMAIN || "tktic.com").trim().toLowerCase();
}

// The runtime apex the backend reported, once we've seen it. Persisted because
// cross-host navigation is a full document load: without it every jump would
// re-run on the baked value until the settings query resolves again.
const ADOPTED_APEX_KEY = "tktic_runtime_apex";
let adoptedApex: string | null | undefined; // undefined = not read from storage yet

function readAdoptedApex(): string | null {
  if (adoptedApex !== undefined) return adoptedApex;
  if (typeof window === "undefined") return (adoptedApex = null);
  try {
    adoptedApex = window.localStorage.getItem(ADOPTED_APEX_KEY);
  } catch {
    adoptedApex = null;
  }
  return adoptedApex;
}

/** The registrable apex domain in force (`tktic.com` in prod, `tktic.dev`
 * on the dev channel). Prefers the apex the BACKEND reported for this
 * deployment over the one baked into the bundle; see `adoptApexDomain`. Use it
 * to build `<slug>.<apex>` URLs so cross-subdomain navigation lands on the
 * right host per environment. */
export function apexDomain(): string {
  return readAdoptedApex() || bakedApexDomain();
}

/** Test seam: forget the adopted apex so a case can start from the baked one. */
export function __resetAdoptedApexForTests(): void {
  adoptedApex = undefined;
}

// A bare registrable domain — at least two labels, no scheme/port/path. The
// value is same-origin backend config rather than user input, but it is
// interpolated into `https://<sub>.<apex>` URLs, so validate before adopting.
const APEX_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

/** The leftmost label of `host` under the apex, or null for the apex / a
 * foreign host (mirrors backend `_subdomain_label`). */
export function subdomainLabel(host: string): string | null {
  const apex = apexDomain();
  if (!host || host === apex) return null;
  const suffix = "." + apex;
  if (!host.endsWith(suffix)) return null; // foreign host (localhost, IP, …)
  const label = host.slice(0, -suffix.length);
  if (!label) return null;
  return label.split(".")[0];
}

/**
 * Cross-check the build-time-baked apex (`apexDomain()`) against the backend's
 * runtime apex (`/api/settings/public.club_apex_domain`) and report a mismatch.
 *
 * The frontend classifies apex/admin/club hosts from `VITE_CLUB_APEX_DOMAIN`,
 * baked in at build time; the backend classifies the SAME hosts from its
 * runtime `TKTIC_CLUB_APEX_DOMAIN`. If a bundle built for one environment is
 * deployed against a backend configured for another (e.g. a prod bundle on the
 * dev host), the two taxonomies disagree and route apex/admin/login through
 * the wrong boundary (#567).
 *
 * #567 deliberately made this REPORT ONLY, reasoning that the baked value is
 * what every already-rendered `<slug>.<apex>` URL on the page was built from,
 * so correcting mid-flight would leave the DOM inconsistent with routing. That
 * reasoning was sound but the conclusion is now too weak: since #593 the
 * operator console is admin-host-only, so a misclassified host doesn't just
 * mis-link — it 404s `/super-admin`, and `HostGuard` bounces `/login` away from
 * the very host that could fix it. Report-only turns a bad build arg into a
 * locked-out deployment.
 *
 * So it now ADOPTS: the backend's value is derived from the deployment's own
 * runtime config and cannot drift from it, which makes it strictly better
 * evidence than a build arg. The DOM-consistency objection is answered by the
 * CALLER, which reloads once after a successful adoption (see `useBrandSync`)
 * — nothing rendered from the stale apex survives. Loop-proof: after the
 * reload the adopted value agrees, so this returns false.
 *
 * Returns `true` only when it adopted a NEW value (i.e. drift was corrected);
 * `false` when the values already agree, or when there is nothing usable to
 * adopt. Validates the shape first — the value is same-origin backend config,
 * not user input, but it lands in `https://<sub>.<apex>` URLs.
 */
export function adoptApexDomain(backendApex: string | null | undefined): boolean {
  const backend = (backendApex ?? "").trim().toLowerCase();
  if (!backend || !APEX_RE.test(backend)) return false; // nothing usable to adopt
  if (backend === apexDomain()) return false; // already agree — the common case
  // eslint-disable-next-line no-console
  console.error(
    `[tktic] apex-domain mismatch: this bundle was built with ` +
      `VITE_CLUB_APEX_DOMAIN="${bakedApexDomain()}" but the backend reports ` +
      `"${backend}". Adopting the backend's value — it is derived from the ` +
      `deployment's own runtime config and cannot drift from it. Rebuild with ` +
      `the correct VITE_CLUB_APEX_DOMAIN to remove this warning.`,
  );
  adoptedApex = backend;
  try {
    window.localStorage.setItem(ADOPTED_APEX_KEY, backend);
  } catch {
    // best-effort; the in-memory value still fixes this document
  }
  return true;
}

export function hostKind(): HostKind {
  // A dev club override pins the session to a club host (the DevClubSwitcher
  // path), regardless of the real localhost hostname. Checked FIRST so a stack
  // that arms both overrides still puts club specs on a club host.
  if (getDevClubOverride()) return "club";
  const host =
    typeof window !== "undefined"
      ? window.location.hostname.toLowerCase()
      : "";
  const label = subdomainLabel(host);
  // Bare apex / www / localhost / foreign. The dev admin-host override applies
  // only here: single-localhost has no subdomains, so this is the one bucket
  // that needs to be able to impersonate `admin.<apex>` for the operator
  // console. A real club subdomain below is deliberately left alone.
  if (label === null) return getDevAdminHostOverride() ? "admin" : "apex";
  if (label === "admin") return "admin";
  if (label === "www") return getDevAdminHostOverride() ? "admin" : "apex";
  return "club";
}

export function isAdminHost(): boolean {
  return hostKind() === "admin";
}

export function isApexHost(): boolean {
  return hostKind() === "apex";
}

/** The club slug of the current host, or null on the apex/admin/foreign host. */
export function currentClubSlug(): string | null {
  if (typeof window === "undefined") return null;
  const label = subdomainLabel(window.location.hostname.toLowerCase());
  if (label === null || label === "admin" || label === "www") return null;
  return label;
}

// --- Social-login originating-club handoff --------------------------------
//
// OIDC social login LEAVES the origin host: the browser bounces to the
// provider (Google/Apple) and returns to the FIXED apex callback
// (`<apex>/kratos/.../callback/...`), because a provider redirect_uri must be
// a single pre-registered URL — never per-club. Kratos then does not reliably
// carry the login flow's `return_to` back to the club through that OIDC
// hand-off (observed: it lands on `<apex>/login`), so the club a user started
// on is lost. We stash the originating club slug in a cookie on the SHARED
// registrable domain (`.<apex>`) before leaving — sessionStorage/localStorage
// are per-origin and would NOT be readable back on the apex — and read it on
// the apex landing to send the browser to the right club host.
const SOCIAL_ORIGIN_CLUB_COOKIE = "tktic_social_origin_club";

// A super-admin can START a social login on the OPERATOR host (`admin.<apex>`),
// which has no club slug to stash. `currentClubSlug()` is null there, so the
// club cookie above no-ops and the apex landing would fall back to a club
// membership — bouncing the operator to a club host where `/super-admin` 404s.
// We instead remember the operator origin as a BOOLEAN marker (never a host
// string — a slug/host value would reopen the open-redirect class the club
// cookie is hardened against; `goToAdminHost` builds the host from a trusted
// literal). Same shared `.<apex>` cookie so it survives the apex round-trip.
const SOCIAL_ORIGIN_ADMIN_COOKIE = "tktic_social_origin_admin";

// Mirror of the backend club-slug charset (`services/clubs.py` `_SLUG_RE`).
// A club slug is interpolated into a redirect URL host, and the source is a
// user-writable cookie (scoped to the shared registrable domain, so any
// subdomain context can set it) — so a value like `evil.com%2f` would decode
// to `evil.com/` and let `https://${slug}.${apex}/…` parse as host=evil.com.
// Reject anything that isn't a bare lowercase slug BEFORE it reaches a URL.
const CLUB_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

/** True for a syntactically-valid club slug (no dots, slashes, `@`, etc.). */
export function isValidClubSlug(slug: string | null | undefined): slug is string {
  return typeof slug === "string" && CLUB_SLUG_RE.test(slug);
}

function cookieDomainAttr(): string {
  // Scope to the registrable apex so the value is readable across every
  // `<slug>.<apex>` host AND the bare apex. Mirrors SESSION_COOKIE_DOMAIN.
  const apex = apexDomain();
  // No leading dot for a bare/localhost host (dev-server has no real apex).
  return apex && apex.includes(".") ? `; Domain=.${apex}` : "";
}

/** Remember the club a social-login flow started on, so the apex landing can
 * return the user there. Short-lived — it only needs to outlive one OIDC
 * round-trip. No-op for a non-club slug. */
export function stashSocialOriginClub(slug: string | null): void {
  if (typeof document === "undefined" || !slug) return;
  document.cookie =
    `${SOCIAL_ORIGIN_CLUB_COOKIE}=${encodeURIComponent(slug)}` +
    `; Path=/; Max-Age=600; SameSite=Lax${cookieDomainAttr()}`;
}

/** Read (and clear) the stashed originating club slug, if any. */
export function takeSocialOriginClub(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${SOCIAL_ORIGIN_CLUB_COOKIE}=`));
  // Always clear, even on a miss-shaped value, so a stale cookie can't loop.
  document.cookie =
    `${SOCIAL_ORIGIN_CLUB_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${cookieDomainAttr()}`;
  if (!match) return null;
  const raw = match.slice(SOCIAL_ORIGIN_CLUB_COOKIE.length + 1);
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return null;
  }
  // The cookie is user-writable; only ever hand back a valid bare slug so a
  // crafted value can't become an off-domain redirect target downstream.
  return isValidClubSlug(decoded) ? decoded : null;
}

/** Full-document navigation to a club host (fresh cache + shared session
 * cookie carry over) — the same pattern as the DevClubSwitcher / open-console
 * link. `path` defaults to the role-router landing. No-op for an invalid slug
 * (defense in depth — every caller should pass a validated slug already). */
export function goToClubHost(slug: string, path = "/hub"): void {
  if (typeof window === "undefined" || !isValidClubSlug(slug)) return;
  // Force `path` to a single-slash-rooted relative path so a protocol-relative
  // (`//evil.com`) or absolute value can't break out of the club host.
  const safePath = "/" + String(path ?? "").replace(/^\/+/, "");
  window.location.assign(`https://${slug}.${apexDomain()}${safePath}`);
}

/** Mark that a social-login flow started on the OPERATOR host, so the apex
 * landing returns the super-admin there instead of a club home. */
export function stashSocialOriginAdmin(): void {
  if (typeof document === "undefined") return;
  document.cookie =
    `${SOCIAL_ORIGIN_ADMIN_COOKIE}=1` +
    `; Path=/; Max-Age=600; SameSite=Lax${cookieDomainAttr()}`;
}

/** Read (and clear) the operator-origin marker. True ONLY for the exact
 * literal `1` — the cookie is user-writable, so any other value reads as
 * absent (the marker is a boolean, never interpolated into a URL). */
export function takeSocialOriginAdmin(): boolean {
  if (typeof document === "undefined") return false;
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${SOCIAL_ORIGIN_ADMIN_COOKIE}=`));
  // Always clear, even on a miss-shaped value, so a stale marker can't loop.
  document.cookie =
    `${SOCIAL_ORIGIN_ADMIN_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${cookieDomainAttr()}`;
  if (!match) return false;
  return match.slice(SOCIAL_ORIGIN_ADMIN_COOKIE.length + 1) === "1";
}

/** Full-document navigation to the OPERATOR host (`admin.<apex>`). The host is
 * built from the trusted literal `admin` + the build's apex — NO user input in
 * the host — so, unlike the club path, there is no slug to validate. `path`
 * defaults to `/super-admin`; it is slash-rooted so a protocol-relative /
 * absolute value can't break out of the admin host. */
export function goToAdminHost(path = "/super-admin"): void {
  if (typeof window === "undefined") return;
  const safePath = "/" + String(path ?? "").replace(/^\/+/, "");
  window.location.assign(`https://admin.${apexDomain()}${safePath}`);
}

/** Stash the social-login origin appropriate to the CURRENT host, so the apex
 * landing can return the user there. A CLUB host stashes its slug; the OPERATOR
 * (`admin`) host — which has no club slug — sets the admin marker instead. The
 * two are mutually exclusive by host; keeping the dispatch here (not inline in
 * the panel) makes the host→marker mapping directly testable. No-op on the apex
 * (nothing to remember). */
export function stashSocialOrigin(): void {
  if (isAdminHost()) {
    stashSocialOriginAdmin();
    return;
  }
  stashSocialOriginClub(currentClubSlug());
}
